import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Bitcoin, ShieldOff, Loader2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import SuggestPortfolio from "@/components/SuggestPortfolio";

const CATS = ["forestry","renewable","ocean","agriculture","direct_air"];

const CURRENCIES = [
  ["usd","US Dollar","$"],["eur","Euro","€"],["gbp","British Pound","£"],["inr","Indian Rupee","₹"],
  ["jpy","Japanese Yen","¥"],["aud","Australian","A$"],["cad","Canadian","C$"],["sgd","Singapore","S$"],
  ["brl","Brazilian","R$"],["aed","AED","د.إ"],["chf","Swiss Franc","CHF"],["mxn","Mexican","MX$"],
  ["zar","South African","R"],["hkd","Hong Kong","HK$"],
];

export default function Cart() {
  const { items, update, remove, clear, usdTotal } = useCart();
  const { format, currency: globalCurrency } = useCurrency();
  const { user } = useAuth();
  const nav = useNavigate();
  const [currency, setCurrency] = useState(globalCurrency || "usd");
  const [crypto, setCrypto] = useState(false);
  const [anonymous, setAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!user) { nav("/login"); return; }
    if (items.length === 0) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/portfolio-checkout", {
        items: items.map((i) => ({ project_id: i.project_id, tons: Number(i.tons) })),
        origin_url: window.location.origin,
        currency,
        include_crypto: crypto,
        anonymous,
        country: user.country || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-center mb-8">
          <ShoppingBag className="w-12 h-12 text-stone-300 mx-auto"/>
          <h1 className="mt-4 text-3xl tracking-tighter font-light">Build a portfolio</h1>
          <p className="text-stone-600 mt-1">Start from a suggestion or browse projects manually.</p>
        </div>
        <SuggestPortfolio />
        <div className="text-center mt-8">
          <Link to="/marketplace"><Button variant="ghost" className="text-[#2C5530]">…or browse the full marketplace →</Button></Link>
        </div>
      </div>
    );
  }

  // Portfolio diversity
  const catSet = new Set(items.map((i) => i.category));
  const totalTons = items.reduce((s, i) => s + Number(i.tons), 0);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-stone-500">Fleet portfolio</div>
      <h1 className="mt-2 text-4xl sm:text-5xl tracking-tighter font-light">Retire {totalTons.toLocaleString()} tons in one deal</h1>
      <p className="text-stone-600 mt-2">You've mixed {items.length} projects across {catSet.size} categories. Diversification lowers risk.</p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-10">
        <div className="lg:col-span-2 space-y-3">
          {items.map((i) => (
            <div key={i.project_id} data-testid={`cart-row-${i.project_id}`} className="bg-white border border-stone-200 rounded-2xl p-5 flex items-center gap-4">
              <img src={i.image_url} alt="" className="w-20 h-20 rounded-xl object-cover"/>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{i.name}</div>
                <div className="text-xs text-stone-500 capitalize mt-0.5">{i.category.replace("_"," ")} · {format(i.price_per_ton)}/t</div>
              </div>
              <div className="flex items-center gap-2">
                <Input type="number" min={1} max={i.available_tons} value={i.tons} onChange={(e) => update(i.project_id, e.target.value)} className="h-10 w-24"/>
                <span className="text-xs text-stone-500">t</span>
              </div>
              <div className="text-lg font-light w-28 text-right">{format(i.price_per_ton * i.tons)}</div>
              <button onClick={() => remove(i.project_id)} className="text-stone-400 hover:text-red-500" data-testid={`row-remove-${i.project_id}`}>
                <Trash2 className="w-4 h-4"/>
              </button>
            </div>
          ))}
          <div className="pt-2">
            <button onClick={clear} className="text-xs text-stone-500 hover:text-red-600" data-testid="clear-cart">Clear all</button>
          </div>
        </div>

        <aside className="h-fit lg:sticky lg:top-24 bg-white border border-stone-200 rounded-2xl p-6">
          <div className="text-sm font-medium mb-4">Checkout</div>

          <div>
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Pay in</div>
            <Select value={currency} onValueChange={setCurrency} disabled={crypto}>
              <SelectTrigger data-testid="cart-currency" className="h-10 bg-stone-50 border-stone-200"><SelectValue/></SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                {CURRENCIES.map(([c,n,s]) => (
                  <SelectItem key={c} value={c}>{s} · {n} ({c.toUpperCase()})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-4 flex items-center justify-between p-3 rounded-xl bg-stone-50 border border-stone-200">
            <div className="flex items-center gap-2">
              <Bitcoin className="w-4 h-4 text-[#D97757]"/>
              <div className="text-sm">Pay with crypto</div>
            </div>
            <Switch checked={crypto} onCheckedChange={setCrypto} data-testid="cart-crypto"/>
          </div>

          <div className="mt-3 flex items-center justify-between p-3 rounded-xl bg-stone-50 border border-stone-200">
            <div className="flex items-center gap-2">
              <ShieldOff className="w-4 h-4 text-stone-600"/>
              <div>
                <div className="text-sm">Retire anonymously</div>
                <div className="text-xs text-stone-500">Hide company name on the public certificate</div>
              </div>
            </div>
            <Switch checked={anonymous} onCheckedChange={setAnonymous} data-testid="cart-anonymous"/>
          </div>

          <div className="mt-6 pt-4 border-t border-stone-200 flex items-baseline justify-between">
            <div className="text-sm text-stone-500">Total ({totalTons.toLocaleString()} tons)</div>
            <div className="text-3xl font-light tracking-tight text-[#2C5530]" data-testid="cart-total">{format(usdTotal)}</div>
          </div>

          <Button onClick={checkout} disabled={loading} data-testid="cart-checkout-final" className="w-full mt-5 h-12 bg-[#2C5530] hover:bg-[#1F3D22] text-white rounded-full">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Redirecting…</> : (user ? "Retire portfolio" : "Sign in to checkout")}
          </Button>

          <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-stone-500">
            <div className="p-3 bg-stone-50 rounded-lg">
              <div className="uppercase tracking-widest">Projects</div>
              <div className="text-lg text-stone-800 font-light">{items.length}</div>
            </div>
            <div className="p-3 bg-stone-50 rounded-lg">
              <div className="uppercase tracking-widest">Categories</div>
              <div className="text-lg text-stone-800 font-light">{catSet.size}</div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
